import ENV from 'config/environment'
import { useEffect } from 'react'
import { ReactElement } from 'react-markdown/lib/react-markdown'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams, useRouteMatch } from 'react-router'
import get from 'lodash/get'
import styled from 'styled-components'
import { H, P, Divider, Wrapper, Button, Grid } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import ExternalLink from 'components/external-link'
import {
  formatAmountToPence,
  getInitialValuesFromInvoice,
} from 'components/payments/invoices/methods'
import PaymentModal, {
  FormOutputValues,
} from 'components/payments/payment-modal'
import Table from 'components/table'
import useInvoices from 'hooks/useInvoices'
import { formatCurrency } from 'lib/formatting/currency'
import { formatDate } from 'lib/formatting/dates'
import { launchPciPalSession } from 'lib/pci-pal/launch-session'
import { FuneralPlan, FuneralPlanPaymentDetails } from 'lib/models/funeral-plan'
import {
  FUNERAL_PLAN_INVOICE_LINE_ITEM_DESCRIPTION,
  Invoice,
  InvoiceAttributes,
} from 'lib/models/invoice'
import { FuneralPlanPaymentType, Lead } from 'lib/models/lead'
import {
  fetchFuneralPlanPaymentDetails,
  generatePciPalSession,
  hideModal,
  showModal,
} from 'state/actions'
import {
  formatToHuman,
  squareUrlForInvoicePage,
  squareUrlForTransactionPage,
  stripeUrlForCustomerPage,
  stripeUrlForInvoicePage,
  stripeUrlForTransactionPage,
} from 'utils/helpers'
import BalancePaymentModal, {
  type ThirdPartyPayer,
} from './balance-payment-modal'
import { CancelFuneralPlan } from './cancel-funeral-plan'
import SetupOnStripeModal from './setup-on-stripe-modal'

const StyledRowWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: row;
  margin: ${GTR.M} 0;
  justify-content: space-between;
  align-items: baseline;
`

const StyledButtonWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: row;
  gap: ${GTR.M};
  align-items: baseline;
`

const StyledColumnWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: baseline;
`

const StyledButton = styled(Button.Primary)`
  margin-top: 10px;
  min-width: 215px;
`

const SquareLink = ({
  reference,
  paymentProviderInvoiceId,
}: {
  reference?: string
  paymentProviderInvoiceId?: string
}): ReactElement => {
  const description = 'Card payment'

  if (paymentProviderInvoiceId) {
    return (
      <ExternalLink href={squareUrlForInvoicePage(paymentProviderInvoiceId)}>
        {description}
      </ExternalLink>
    )
  }

  if (!paymentProviderInvoiceId && reference) {
    return (
      <ExternalLink href={squareUrlForTransactionPage(reference)}>
        {description}
      </ExternalLink>
    )
  }

  return <P>No Square link</P>
}

const StripeLink = ({
  reference,
  paymentProviderInvoiceId,
}: {
  reference?: string
  paymentProviderInvoiceId?: string
}): ReactElement => {
  const description = 'Card payment'

  if (reference) {
    return (
      <ExternalLink href={stripeUrlForTransactionPage(reference)}>
        {description}
      </ExternalLink>
    )
  }

  /** reference takes precedence over invoiceId - all paid invoices will have a reference, and invoiceId will be used for unpaid ones */
  if (paymentProviderInvoiceId) {
    return (
      <ExternalLink href={stripeUrlForInvoicePage(paymentProviderInvoiceId)}>
        {description}
      </ExternalLink>
    )
  }
  return <P>No Stripe link</P>
}

const PaymentLink = ({
  paymentProvider,
  reference,
  paymentProviderInvoiceId,
}: {
  paymentProvider: string
  reference?: string
  paymentProviderInvoiceId?: string
}): ReactElement => {
  if (paymentProvider === 'square') {
    return (
      <SquareLink
        reference={reference}
        paymentProviderInvoiceId={paymentProviderInvoiceId}
      />
    )
  }

  if (paymentProvider === 'stripe') {
    return (
      <StripeLink
        reference={reference}
        paymentProviderInvoiceId={paymentProviderInvoiceId}
      />
    )
  }

  return <P>No {formatToHuman(paymentProvider)} link</P>
}

const getInvoiceStatus = (
  attributes: Invoice['attributes']
): 'Paid' | 'Planned' | 'Overdue' | 'Refund in progress' | 'Refunded' => {
  const { lineItems, paidOn, issuedDate } = attributes

  if (
    lineItems.find(
      (item) =>
        item.description ===
          FUNERAL_PLAN_INVOICE_LINE_ITEM_DESCRIPTION.refund ||
        item.description ===
          FUNERAL_PLAN_INVOICE_LINE_ITEM_DESCRIPTION.dispute_funds_withdrawn
    )
  ) {
    return 'Refunded'
  }
  if (
    lineItems.find(
      (item) =>
        item.description ===
        FUNERAL_PLAN_INVOICE_LINE_ITEM_DESCRIPTION.refund_in_progress
    )
  ) {
    return 'Refund in progress'
  }
  if (paidOn) {
    return 'Paid'
  }
  return new Date(issuedDate as string) < new Date() ? 'Overdue' : 'Planned'
}

const getInvoiceAmount = (
  lineItems: Invoice['attributes']['lineItems']
): string => {
  const totalPrice = lineItems.reduce(
    (acc, lineItem) => acc + lineItem.price,
    0
  )

  return formatCurrency({
    showPence: true,
    value: totalPrice,
    valueInPence: true,
  })
}

const Payment = (): React.ReactElement => {
  const { id: funeralPlanId } = useParams<{ id: string }>()
  const showCancelPlan = useRouteMatch({
    path: '/funeral-plans/:id/payment/cancel-plan',
    exact: true,
  })?.isExact

  const dispatch = useDispatch()
  const { funeralPlan, funeralPlanPaymentDetails } = useSelector(
    (state: {
      funeralPlanCase: FuneralPlan
      funeralPlanPaymentDetails: FuneralPlanPaymentDetails
    }) => ({
      funeralPlan: state.funeralPlanCase,
      funeralPlanPaymentDetails: state.funeralPlanPaymentDetails,
    }),
    shallowEqual
  )

  useEffect(() => {
    dispatch(fetchFuneralPlanPaymentDetails(funeralPlanId))
  }, [funeralPlanId, dispatch])

  const {
    invoices,
    fetchInvoices,
    updateInvoice,
    updateLineItem,
    deleteInvoice,
  } = useInvoices()

  useEffect(() => {
    if (funeralPlanId) {
      fetchInvoices({ queryParams: { 'filter[funeralPlanId]': funeralPlanId } })
    }
  }, [fetchInvoices, funeralPlanId])

  useEffect(() => {
    if (!showCancelPlan || !funeralPlanPaymentDetails.type) return

    const inputEl = document.getElementById('cancel-plan')
    if (!inputEl) return

    const topOfSection = inputEl.getBoundingClientRect().top + window.scrollY
    const pointToScrollTo = topOfSection - parseInt(GTR.XL)
    window.scrollTo(0, pointToScrollTo)
  }, [showCancelPlan, funeralPlanPaymentDetails])

  if (!funeralPlanPaymentDetails.type) {
    return <></>
  }

  const handleGoToCustomerStripe = async () => {
    if (!funeralPlanPaymentDetails.attributes.stripeCustomerId) return

    window.open(
      stripeUrlForCustomerPage(
        funeralPlanPaymentDetails.attributes.stripeCustomerId
      )
    )
  }

  const openPciPalTab = async ({
    referenceId,
    paymentType,
    amountInPence,
    thirdPartyPayer,
  }: {
    referenceId: Lead['attributes']['uuid']
    paymentType: FuneralPlanPaymentType
    amountInPence: number
    /** A third party paying on behalf of the purchaser. Omitted when the purchaser pays. */
    thirdPartyPayer?: ThirdPartyPayer
  }): Promise<void> => {
    try {
      const response = await dispatch(
        generatePciPalSession({
          referenceId,
          paymentType,
          amountInPence,
          product: 'funeral_plan',
          thirdPartyPayer,
        })
      )

      const data = get(response, 'value.data.data')
      const url = get(data, 'url')
      const accessToken = get(data, 'accessToken')
      const refreshToken = get(data, 'refreshToken')
      launchPciPalSession({ url, accessToken, refreshToken })
    } catch {
      dispatch(hideModal())
    }
  }

  const handlePayBalanceByPciPalModal = ({
    referenceId,
    amountInPence,
  }: {
    referenceId: Lead['attributes']['uuid']
    amountInPence: number
  }): void => {
    dispatch(
      showModal({
        component: BalancePaymentModal,
        headingText: 'Take balance payment with PCI PAL',
        config: {
          amountInPence,
          openPciPalTab: (thirdPartyPayer?: ThirdPartyPayer) =>
            openPciPalTab({
              referenceId,
              paymentType: 'in_full',
              amountInPence,
              thirdPartyPayer,
            }),
        },
      })
    )
  }

  const handleSetupOnStripeModal = ({
    referenceId,
    monthlyAmountInPence,
  }: {
    referenceId: Lead['attributes']['uuid']
    monthlyAmountInPence: number
  }): void => {
    dispatch(
      showModal({
        component: SetupOnStripeModal,
        headingText: 'Set customer up on Stripe',
        config: {
          monthlyAmountInPence,
          openPciPalTab: () =>
            openPciPalTab({
              referenceId,
              paymentType: 'instalments',
              amountInPence: monthlyAmountInPence,
            }),
        },
      })
    )
  }

  const editPayment = (invoice: Invoice) => {
    const formType = 'card'
    const MODAL_MAX_WIDTH = 650

    dispatch(
      showModal({
        component: PaymentModal,
        headingText: 'Edit payment',
        maxWidth: MODAL_MAX_WIDTH,
        config: {
          type: formType,
          values: getInitialValuesFromInvoice({ type: formType, invoice }),
          hideModal: () => dispatch(hideModal()),
          deletePayment: async () => {
            await deleteInvoice(invoice.id)
            dispatch(fetchFuneralPlanPaymentDetails(funeralPlanId))
            fetchInvoices({
              queryParams: { 'filter[funeralPlanId]': funeralPlanId },
            })
          },
          savePayment: async ({
            reference,
            date,
            amount,
          }: FormOutputValues) => {
            const attributes = {
              reference,
              issuedDate: date,
            } as Partial<InvoiceAttributes>

            attributes.paidOn = attributes.issuedDate

            const price = formatAmountToPence(amount)
            await updateLineItem(invoice.attributes.lineItems[0], {
              price,
            })

            await updateInvoice(invoice.id, attributes)
            dispatch(fetchFuneralPlanPaymentDetails(funeralPlanId))
            fetchInvoices({
              queryParams: { 'filter[funeralPlanId]': funeralPlanId },
            })
          },
        },
      })
    )
  }

  const paidInvoices =
    invoices?.filter((invoice: Invoice) => invoice?.attributes.paidOn) ?? []
  const hasOnlySquarePayments =
    paidInvoices.length > 0 &&
    paidInvoices.every(
      (invoice: Invoice) => invoice.attributes.paymentProvider === 'square'
    )

  const shouldShowSetupOnStripeButton =
    ENV.FF_STRIPE_SWITCH_BUTTON_ENABLED &&
    funeralPlan.attributes.status === 'active' &&
    funeralPlanPaymentDetails.attributes.type === 'Instalments' &&
    !['Paid', 'Refunded'].includes(
      funeralPlanPaymentDetails.attributes.status
    ) &&
    hasOnlySquarePayments

  return (
    <Wrapper>
      <H tag="h2" size="M">
        Payments
      </H>
      <Grid>
        <Grid.Item span={6}>
          <H tag="h3" size="S">
            Sale
          </H>
          <Table>
            <Table.Row gridTemplateCols="1fr 1fr">
              <Table.Col>Left to pay</Table.Col>
              <Table.Col strong>
                {formatCurrency({
                  showPence: true,
                  value: funeralPlanPaymentDetails.attributes.leftToPayInPence,
                  valueInPence: true,
                })}
              </Table.Col>
            </Table.Row>
            <Table.Row gridTemplateCols="1fr 1fr">
              <Table.Col>Unpaid invoices</Table.Col>
              <Table.Col>
                {formatCurrency({
                  showPence: true,
                  value:
                    funeralPlanPaymentDetails.attributes.unpaidInvoicesInPence,
                  valueInPence: true,
                })}
              </Table.Col>
            </Table.Row>
            <Table.Row gridTemplateCols="1fr 1fr">
              <Table.Col>Total quoted</Table.Col>
              <Table.Col>
                {formatCurrency({
                  showPence: true,
                  value: funeralPlanPaymentDetails.attributes.totalPriceInPence,
                  valueInPence: true,
                })}
              </Table.Col>
            </Table.Row>
            <Table.Row gridTemplateCols="1fr 1fr">
              <Table.Col>Total paid</Table.Col>
              <Table.Col strong>
                {formatCurrency({
                  showPence: true,
                  value: funeralPlanPaymentDetails.attributes.totalPaidInPence,
                  valueInPence: true,
                })}
              </Table.Col>
            </Table.Row>
          </Table>
        </Grid.Item>
        <Grid.Item span={6}>
          <H tag="h3" size="S">
            Status
          </H>
          <Table>
            <Table.Row gridTemplateCols="1fr 1fr">
              <Table.Col>Status</Table.Col>
              <Table.Col strong>
                {funeralPlanPaymentDetails.attributes.status}
              </Table.Col>
            </Table.Row>
            <Table.Row gridTemplateCols="1fr 1fr">
              <Table.Col>Type</Table.Col>
              <Table.Col>{funeralPlanPaymentDetails.attributes.type}</Table.Col>
            </Table.Row>
            {funeralPlanPaymentDetails.attributes.type === 'Instalments' && (
              <Table.Row gridTemplateCols="1fr 1fr">
                <Table.Col>Instalments paid</Table.Col>
                <Table.Col>
                  {funeralPlanPaymentDetails.attributes.numberOfInstalmentsPaid}{' '}
                  of{' '}
                  {
                    funeralPlanPaymentDetails.attributes
                      .numberOfInstalmentsTotal
                  }
                </Table.Col>
              </Table.Row>
            )}
          </Table>
        </Grid.Item>
        <StyledRowWrapper margin={['L', 0, 0]}>
          {/* If there is an outstanding balance, show the "Pay Balance" button,
           * even if the customer did not pay by Stripe before. */}
          {!['Paid', 'Refunded'].includes(
            funeralPlanPaymentDetails.attributes.status
          ) ? (
            <StyledButtonWrapper>
              {
                /* Prevents Farewill plans with negative outstanding balance (due to auto-calculation of £100 instalment fee refund) from being shown "Pay Balance" button */
                funeralPlanPaymentDetails.attributes
                  .amountToClearBalanceInPence &&
                  funeralPlanPaymentDetails.attributes
                    .amountToClearBalanceInPence > 0 && (
                    <StyledButton
                      onClick={() =>
                        handlePayBalanceByPciPalModal({
                          referenceId: funeralPlan.attributes
                            .leadUuid as string,
                          amountInPence:
                            funeralPlanPaymentDetails.attributes
                              .amountToClearBalanceInPence || 0,
                        })
                      }
                    >
                      Pay Balance
                    </StyledButton>
                  )
              }
              {shouldShowSetupOnStripeButton && (
                <StyledButton
                  onClick={() =>
                    handleSetupOnStripeModal({
                      referenceId: funeralPlan.attributes.leadUuid as string,
                      monthlyAmountInPence:
                        funeralPlanPaymentDetails.attributes
                          .monthlyPriceInPence || 0,
                    })
                  }
                >
                  Set customer up on Stripe
                </StyledButton>
              )}
            </StyledButtonWrapper>
          ) : (
            <></>
          )}
        </StyledRowWrapper>
      </Grid>

      {invoices && (
        <>
          <Divider margin={['XL', 0]} />

          <Table withHeaders>
            <H size="S">Payments</H>
            <Table.Row gridTemplateCols="1fr 1fr 1.5fr 1fr 1fr 0.5fr">
              <Table.Col strong>Date</Table.Col>
              <Table.Col strong>Provider</Table.Col>
              <Table.Col strong>Description</Table.Col>
              <Table.Col strong>Status</Table.Col>
              <Table.Col strong>Amount</Table.Col>
            </Table.Row>
            {invoices.map((invoice: Invoice) => {
              if (!invoice) return null

              const { attributes } = invoice
              const status = getInvoiceStatus(attributes)
              const amount = getInvoiceAmount(attributes.lineItems)
              return (
                <Table.Row
                  gridTemplateCols="1fr 1fr 1.5fr 1fr 1fr 0.5fr"
                  key={invoice.id}
                >
                  <P>{formatDate(attributes.issuedDate)}</P>
                  <P>{formatToHuman(attributes.paymentProvider)}</P>
                  <PaymentLink
                    paymentProvider={attributes?.paymentProvider}
                    reference={attributes?.reference}
                    paymentProviderInvoiceId={
                      attributes?.paymentProviderInvoiceId
                    }
                  />
                  <P>{status}</P>
                  <P>{amount}</P>
                  {/* we will only allow manual editing of Square Virtual Terminal invoices, 
                  as refunds for these won't be recorded automatically */}
                  {attributes.paymentProvider === 'square' &&
                    !attributes?.paymentProviderInvoiceId && (
                      <Button onClick={() => editPayment(invoice)}>edit</Button>
                    )}
                </Table.Row>
              )
            })}
          </Table>
        </>
      )}
      {['open', 'active'].includes(funeralPlan.attributes.status) && (
        <StyledRowWrapper>
          <StyledColumnWrapper>
            {funeralPlanPaymentDetails.attributes.stripeCustomerId && (
              <StyledButton onClick={() => handleGoToCustomerStripe()}>
                Customer on Stripe
              </StyledButton>
            )}
          </StyledColumnWrapper>
          <div>
            <P strong>
              Total paid:{' '}
              {formatCurrency({
                showPence: true,
                value: funeralPlanPaymentDetails.attributes.totalPaidInPence,
                valueInPence: true,
              })}
            </P>
          </div>
        </StyledRowWrapper>
      )}

      <Divider margin={['XL', 0]} />
      <Wrapper>
        <Table withHeaders>
          <H size="S">Quote</H>
          <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
            <Table.Col strong>Description</Table.Col>
            <Table.Col strong>Units</Table.Col>
            <Table.Col strong>Amount</Table.Col>
          </Table.Row>
          <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
            <Table.Col>Funeral plan price</Table.Col>
            <Table.Col>1</Table.Col>
            <Table.Col>
              {formatCurrency({
                showPence: true,
                value: funeralPlanPaymentDetails.attributes.packagePrice,
                valueInPence: true,
              })}
            </Table.Col>
          </Table.Row>
          {funeralPlanPaymentDetails.attributes.celebrationPriceInPence && (
            <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
              <Table.Col>Celebration</Table.Col>
              <Table.Col>1</Table.Col>
              <Table.Col>
                {formatCurrency({
                  showPence: true,
                  value:
                    funeralPlanPaymentDetails.attributes
                      .celebrationPriceInPence,
                  valueInPence: true,
                })}
              </Table.Col>
            </Table.Row>
          )}
          {funeralPlanPaymentDetails.attributes.discountAmountInPence && (
            <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
              <Table.Col>Discount</Table.Col>
              <Table.Col>1</Table.Col>
              <Table.Col>
                -
                {formatCurrency({
                  showPence: true,
                  value:
                    funeralPlanPaymentDetails.attributes.discountAmountInPence,
                  valueInPence: true,
                })}
              </Table.Col>
            </Table.Row>
          )}
          {funeralPlanPaymentDetails.attributes.type === 'Instalments' &&
            funeralPlanPaymentDetails.attributes.numberOfInstalmentsTotal && (
              <>
                <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
                  <Table.Col>Instalments fee</Table.Col>
                  <Table.Col>1</Table.Col>
                  <Table.Col>
                    {formatCurrency({
                      showPence: true,
                      value:
                        funeralPlanPaymentDetails.attributes.instalmentsFee,
                      valueInPence: true,
                    })}
                  </Table.Col>
                </Table.Row>
                <Table.Row gridTemplateCols="1fr 2fr 0.5fr">
                  <Table.Col>Single Instalment</Table.Col>
                  <Table.Col>
                    {
                      funeralPlanPaymentDetails.attributes
                        .numberOfInstalmentsTotal
                    }
                  </Table.Col>
                  <Table.Col>
                    {formatCurrency({
                      showPence: true,
                      value:
                        funeralPlanPaymentDetails.attributes
                          .monthlyPriceInPence,
                      valueInPence: true,
                    })}
                  </Table.Col>
                </Table.Row>
              </>
            )}
        </Table>
      </Wrapper>
      {showCancelPlan && (
        <Wrapper id="cancel-plan" padding={[GTR.XXL, 0]}>
          <CancelFuneralPlan
            invoices={invoices}
            funeralPlanPaymentDetails={funeralPlanPaymentDetails}
            refreshDetails={() => {
              dispatch(fetchFuneralPlanPaymentDetails(funeralPlanId))
            }}
          />
        </Wrapper>
      )}
    </Wrapper>
  )
}

export default Payment
